// Wormholes: the perfectly symmetric chords — diminished sevenths and augmented
// triads — sit at the singular points of voice-leading space, with short exits
// into many keys. Only 3 dim7s and 4 augmented triads exist; each dim7 is the
// hub of Barry Harris's "family of four dominants".

import { useMemo, useState } from 'react';
import { spellNote, type Accidental, type PitchClass } from '@/music/notes';
import { generateChordVoicing } from '@/music/voicing';
import { primaryVoicingFamily } from '@/music/voicingFamilies';
import { getChordPlayer } from '@/audio/player';
import { useStore } from '@/state/store';
import { PlayableDiagram } from '@/components/PlayableDiagram';
import { PitchClassClock } from './PitchClassClock';

const ACCIDENTAL: Accidental = 'flat';

const play = (pcs: PitchClass[], base = 48) => {
  void getChordPlayer().playMidi(
    [...pcs].sort((a, b) => a - b).map((pc) => base + pc),
    0.04,
  );
};

/** The four dominant 7♭9 roots living a major third below each dim7 tone. */
function familyRoots(dimPcs: PitchClass[]): PitchClass[] {
  return dimPcs.map((pc) => (((pc - 4) % 12) + 12) % 12).sort((a, b) => a - b);
}

function DominantCard({ rootPc }: { rootPc: PitchClass }) {
  const stringSet = useStore((s) => s.stringSet);
  const voicingFamily = primaryVoicingFamily(useStore((s) => s.voicingFamily));
  const root = spellNote(rootPc, ACCIDENTAL);
  const resolves = spellNote((((rootPc + 5) % 12) + 12) % 12, ACCIDENTAL); // down a fifth
  const fingering = useMemo(() => {
    for (const position of [0, 1, 2, 3] as const) {
      const candidate = generateChordVoicing(root, 'dom7b9', position, stringSet, voicingFamily);
      if (candidate) return candidate;
    }
    return null;
  }, [root, stringSet, voicingFamily]);
  if (!fingering) return null;
  return (
    <PlayableDiagram
      chord={{
        fingering,
        rootDisplay: root,
        chordType: 'dom7b9',
        symbol: `${root}7♭9`,
        inversion: fingering.inversion,
        stringSet,
      }}
      caption={`wants ${resolves}`}
      showTitle
      variant="card"
    />
  );
}

export function WormholesExhibit() {
  // The three diminished sevenths partition the 12 pitch classes into squares.
  const dims = useMemo(
    () => [0, 1, 2].map((off) => [off, off + 3, off + 6, off + 9] as PitchClass[]),
    [],
  );
  const [dimIndex, setDimIndex] = useState(2); // {2,5,8,11} → the G7 family
  const family = familyRoots(dims[dimIndex]);

  const augs = useMemo(
    () => [0, 1, 2, 3].map((off) => [off, off + 4, off + 8] as PitchClass[]),
    [],
  );

  return (
    <div className="lab-exhibit" data-testid="lab-wormholes">
      <p className="lab-lede">
        A chord that divides the octave into equal parts maps onto itself when transposed by that
        interval, so there are only a few of them. One fingering serves several keys, and each
        resolves to many chords by small moves.
      </p>

      <h3>Only three diminished sevenths exist</h3>
      <p className="muted">
        12 notes divided by 4-fold symmetry gives 3 chords. The °7 grip repeats every three frets:
        moving it a minor third gives the same chord. Tap a square to hear it.
      </p>
      <div className="lab-row lab-clock-row" data-testid="dim-clocks">
        {dims.map((pcs, i) => (
          <div key={i} className={`lab-clock-pick${i === dimIndex ? ' selected' : ''}`}>
            <PitchClassClock
              pcs={pcs}
              accidental={ACCIDENTAL}
              title={`${spellNote(pcs[0], ACCIDENTAL)}°7`}
              onTap={() => {
                setDimIndex(i);
                play(pcs);
              }}
            />
          </div>
        ))}
      </div>

      <h3>Each one is shared by four dominants</h3>
      <p className="muted">
        Add a root a major third below any note of {spellNote(dims[dimIndex][0], ACCIDENTAL)}°7 and
        it becomes a 7♭9 chord, so four dominants share the same diminished seventh (Barry
        Harris&rsquo;s family: {family.map((pc) => spellNote(pc, ACCIDENTAL) + '7').join(', ')}).
        The tritone substitute is one of the four.
      </p>
      <div className="lab-cards" data-testid="family-cards">
        {family.map((pc) => (
          <DominantCard key={pc} rootPc={pc} />
        ))}
      </div>

      <h3>Four augmented triads</h3>
      <p className="muted">
        12 divided by 3-fold symmetry gives 4. The key centres of Giant Steps (B, G and E♭) form one
        of these triads, so the changes between them voice-lead by small steps.
      </p>
      <div className="lab-row lab-clock-row" data-testid="aug-clocks">
        {augs.map((pcs, i) => (
          <PitchClassClock
            key={i}
            pcs={pcs}
            accidental={ACCIDENTAL}
            title={`${spellNote(pcs[0], ACCIDENTAL)}+`}
            onTap={() => play(pcs, 52)}
          />
        ))}
      </div>
    </div>
  );
}
